import { AdjustedLine, ModifierContext } from "../modifier-models";
import { BaseRule } from "../rule-base";

// ===== rules/rule-anaesthetic-core.ts =======================================
export class RuleAnaesMin extends BaseRule {
    name = 'Anaesthetic min / GP reduction / 0032 / 0034';

    run(line: AdjustedLine, ctx: ModifierContext, autoApply: boolean): void {
        if (line.meta?.['discipline'] !== 'anaesthetic') return;

        // ── handle min‑5 rules (0032 / 0034) ───────────────────────────────
        if (line.modifiers?.some(m => m === '0032' || m === '0034')) {
            if (line.adjustedUnits < 5) {
                const diff = 5 - line.adjustedUnits;
                if (autoApply) {
                    line.adjustedUnits = 5;
                    line.trace.push(`${line.modifiers?.includes('0032') ? '0032' : '0034'} min‑5 (+${diff})`);
                } else {
                    line.suggestions = line.suggestions || [];
                    line.suggestions.push({
                        ruleCode: line.modifiers?.includes('0032') ? '0032' : '0034',
                        message: `Minimum 5 units for anaesthetic procedure (+${diff})`,
                        kind: 'overwrite',
                        unitsDelta: 5
                    });
                }
            }
        }

        // ── handle specialist/GP minimums ─────────────────────────────────
        const floor = ctx.minimumUnitFloor ?? 7;
        if (ctx.anaesthetistType === 'specialist' && line.adjustedUnits < floor) {
            const diff = floor - line.adjustedUnits;
            if (autoApply) {
                line.adjustedUnits += diff;
                line.trace.push(`0035 min-${floor} (+${diff})`);
            } else {
                line.suggestions = line.suggestions || [];
                line.suggestions.push({
                    ruleCode: '0035',
                    message: `Specialist anaesthetist minimum ${floor} units (+${diff})`,
                    kind: 'addition',
                    unitsDelta: diff
                });
            }
        } else if (ctx.anaesthetistType === 'gp' && line.adjustedUnits < floor) {
            const diff = floor - line.adjustedUnits;
            if (autoApply) {
                line.adjustedUnits += diff;
                line.trace.push(`0036 min-${floor} (+${diff})`);
            } else {
                line.suggestions = line.suggestions || [];
                line.suggestions.push({
                    ruleCode: '0036',
                    message: `GP anaesthetist minimum ${floor} units (+${diff})`,
                    kind: 'addition',
                    unitsDelta: diff
                });
            }
        }
    }

    apply(lines: AdjustedLine[], ctx: ModifierContext, warn: string[]): AdjustedLine[] {
        const anaes = lines.filter(l => l.meta?.['discipline'] === 'anaesthetic');
        if (!anaes.length) return lines;

        // ── handle min‑5 rules (0032 / 0034) ───────────────────────────────
        anaes.forEach(l => {
            if (l.modifiers?.some(m => m === '0032' || m === '0034')) {
                if (l.adjustedUnits < 5) {
                    const diff = 5 - l.adjustedUnits;
                    l.adjustedUnits = 5;
                    l.trace.push(`${l.modifiers?.includes('0032') ? '0032' : '0034'} min‑5 (+${diff})`);
                }
            }
        });

        // ── compute total after possible min‑5 bumps ───────────────────────
        let total = anaes.reduce((s, l) => s + l.adjustedUnits, 0);
        const floor = ctx.minimumUnitFloor ?? 7;

        if (ctx.anaesthetistType === 'specialist') {
            if (total < floor) {
                const diff = floor - total;
                anaes[0].adjustedUnits += diff;
                anaes[0].trace.push(`0035 min-${floor} (+${diff})`);
                total = floor;
            }
        } else if (ctx.anaesthetistType === 'gp') {
            // 80 % reduction only when >1h (handled externally); enforce min 7
            if (total < floor) {
                const diff = floor - total;
                anaes[0].adjustedUnits += diff;
                anaes[0].trace.push(`0036 min-${floor} (+${diff})`);
                total = floor;
            }
        } else {
            warn.push('anaesthetistType not set - 0035/0036 not enforced.');
        }

        return lines;
    }

    private calculateTimeUnits(minutes: number): number {
        const firstHour = Math.min(60, minutes);
        const remainingMinutes = Math.max(0, minutes - 60);

        const firstHourUnits = Math.ceil(firstHour / 15) * 2;
        const remainingUnits = Math.ceil(remainingMinutes / 15) * 3;

        return firstHourUnits + remainingUnits;
    }
}
